/**
 * Email Broadcast Page — Super-admin only.
 *
 * Wires the admin console to the existing email endpoints:
 *   GET  /api/admin/email/status
 *   POST /api/admin/email/toggle           { enabled: bool }
 *   POST /api/admin/email/test-send        { to_email }
 *   POST /api/admin/email/broadcast        two-step:
 *     step 1: dry_run=true          → { eligible }
 *     step 2: dry_run=false + confirm=true (blocked while sending is OFF)
 *
 * NO endpoints are re-implemented here — this is a thin console.
 * Terminology: entry / prize / withdrawal / boost. Never uses gambling
 * words in any UI copy.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Mail,
  Send,
  Eye,
  ShieldCheck,
  ShieldOff,
  AlertTriangle,
  CheckCircle2,
  Lock,
  RefreshCw,
  Image as ImageIcon,
  Paperclip,
  X,
} from 'lucide-react';


const StatusToggle = ({ status, onToggle, saving }) => {
  const on = !!status?.sending_enabled;
  return (
    <div
      data-testid="broadcast-status-banner"
      className={`rounded-xl border p-4 flex items-center gap-4 ${
        on
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-amber-300 bg-amber-50'
      }`}
    >
      {on ? (
        <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
      ) : (
        <ShieldOff className="w-6 h-6 text-amber-600 shrink-0" />
      )}
      <div className="flex-1">
        <div className="font-semibold text-gray-900">
          Global email sending: {on ? 'ON' : 'OFF'}
        </div>
        <div className="text-sm text-gray-600 mt-0.5">
          {on
            ? `Broadcasts will actually deliver. Provider: ${status?.provider || 'unknown'}.`
            : 'Broadcasts are BLOCKED until this switch is flipped on. Test-send to your own inbox still works.'}
        </div>
      </div>
      <button
        type="button"
        data-testid="broadcast-status-toggle"
        disabled={saving}
        onClick={() => onToggle(!on)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          on
            ? 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100'
            : 'bg-amber-600 text-white hover:bg-amber-700'
        } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {saving ? '…' : on ? 'Turn OFF' : 'Turn ON'}
      </button>
    </div>
  );
};


const EmailBroadcastPage = () => {
  const { api, admin } = useAdmin();

  const isSuperAdmin = admin?.role === 'super_admin';

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [toggleSaving, setToggleSaving] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [eligibleCount, setEligibleCount] = useState(null);
  const [dryRunAt, setDryRunAt] = useState(null);
  // Round 35: who gets it. Default = every address GEMS holds (members + waitlist).
  const [audience, setAudience] = useState('everyone');
  const [audienceInfo, setAudienceInfo] = useState(null);

  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [testSendLoading, setTestSendLoading] = useState(false);
  const [deliverReal, setDeliverReal] = useState(false);

  // Feb 2026: inline images + file attachments for the broadcast.
  // `attachments` = [{ filename, content(base64), content_type, size }].
  const [attachments, setAttachments] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const inlineImgInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const bodyTextareaRef = useRef(null);

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result || '';
      const b64 = String(dataUrl).split(',')[1] || '';
      resolve(b64);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

  const insertAtCursor = (snippet) => {
    // Insert `snippet` into bodyHtml at the current textarea cursor,
    // otherwise append. Keeps caret right after the inserted HTML.
    const ta = bodyTextareaRef.current;
    if (!ta) { setBodyHtml((v) => v + snippet); return; }
    const start = ta.selectionStart ?? bodyHtml.length;
    const end = ta.selectionEnd ?? bodyHtml.length;
    const next = bodyHtml.slice(0, start) + snippet + bodyHtml.slice(end);
    setBodyHtml(next);
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const pos = start + snippet.length;
        ta.setSelectionRange(pos, pos);
      } catch (_) {}
    });
  };

  const handleAddInlineImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Only image files can be inserted inline');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be ≤ 5MB');
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const up = await api.post('/api/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const rawUrl = up.data?.url;
      if (!rawUrl) throw new Error('No URL returned');
      const absoluteUrl = rawUrl.startsWith('http') ? rawUrl : `${window.location.origin}${rawUrl}`;
      const snippet = `<img src="${absoluteUrl}" alt="${file.name.replace(/"/g, '')}" style="max-width:100%;height:auto;display:block;margin:12px 0" />`;
      insertAtCursor(snippet);
      toast.success('Image inserted inline');
    } catch (err) {
      console.error('Inline image upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const ALLOWED = new Set(['image/png','image/jpeg','image/jpg','image/gif','image/webp','application/pdf']);
    const MAX = 5 * 1024 * 1024;
    const room = 3 - attachments.length;
    if (room <= 0) { toast.error('Max 3 attachments'); return; }
    const next = [...attachments];
    for (const f of files.slice(0, room)) {
      if (!ALLOWED.has(f.type)) { toast.error(`${f.name}: type not allowed`); continue; }
      if (f.size > MAX) { toast.error(`${f.name}: exceeds 5MB`); continue; }
      try {
        const content = await readFileAsBase64(f);
        next.push({ filename: f.name, content, content_type: f.type, size: f.size });
      } catch (err) {
        toast.error(`${f.name}: failed to read`);
      }
    }
    setAttachments(next);
  };

  const removeAttachment = (i) => setAttachments((a) => a.filter((_, idx) => idx !== i));

  // -------- Load status ---------------------------------------------
  const refreshStatus = async () => {
    setStatusLoading(true);
    try {
      const r = await api.get('/api/admin/email/status');
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email status');
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) refreshStatus();
  }, [isSuperAdmin]);

  // -------- Toggle --------------------------------------------------
  const handleToggle = async (enabled) => {
    setToggleSaving(true);
    try {
      const r = await api.post('/api/admin/email/toggle', { enabled });
      setStatus((s) => ({ ...(s || {}), sending_enabled: !!r.data.sending_enabled }));
      toast.success(`Global email sending is now ${r.data.sending_enabled ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle sending');
    } finally {
      setToggleSaving(false);
    }
  };

  // -------- Test-send to admin's own inbox --------------------------
  const handleTestSend = async () => {
    if (!admin?.email) {
      toast.error('Your admin email address is not set — cannot test-send.');
      return;
    }
    if (deliverReal) {
      const ok = window.confirm(
        'Deliver a REAL email to your inbox via Resend? This uses one send from your quota (rate-limited to 1 per 15 min).'
      );
      if (!ok) return;
    }
    setTestSendLoading(true);
    try {
      const r = await api.post('/api/admin/email/test-send', {
        to_email: admin.email,
        subject: subject
          ? `[TEST] ${subject}`
          : '[TEST] GEMS Live email deliverability check',
        deliver_real: deliverReal,
        // Feb 2026: send the composed HTML + attachments so the test
        // mirrors exactly what a real recipient would see.
        html: bodyHtml || undefined,
        text: bodyHtml
          ? '[HTML content — please view in an HTML-capable email client]'
          : undefined,
        attachments: attachments.length
          ? attachments.map(a => ({ filename: a.filename, content: a.content, content_type: a.content_type }))
          : undefined,
      });
      if (r.data.success) {
        toast.success(
          r.data.delivered_real
            ? `Real email dispatched to ${admin.email} via Resend`
            : `Mock test logged (no real send) — flip "Deliver real" to send via Resend`
        );
      } else {
        toast.error(r.data.message || 'Provider rejected the test send');
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || 'Rate-limited — try again in a few minutes.');
      } else {
        toast.error(detail || 'Test-send failed');
      }
    } finally {
      setTestSendLoading(false);
    }
  };

  // -------- Dry-run (step 1) ----------------------------------------
  const handleDryRun = async () => {
    if (!subject.trim()) return toast.error('Subject required to check recipients');
    if (!bodyHtml.trim()) return toast.error('Body required to check recipients');
    setDryRunLoading(true);
    setSendResult(null);
    try {
      const r = await api.post('/api/admin/email/broadcast', {
        subject,
        html: bodyHtml,
        audience,
        dry_run: true,
        confirm: false,
      });
      const count = r.data?.eligible ?? 0;
      setEligibleCount(count);
      setAudienceInfo(r.data || null);
      setDryRunAt(new Date().toISOString());
      toast.success(`${count} eligible recipient${count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setDryRunLoading(false);
    }
  };

  // -------- Real send (step 2) --------------------------------------
  const handleConfirmSend = async () => {
    if (eligibleCount == null) return toast.error('Run "Check recipients" first');
    if (!status?.sending_enabled) {
      toast.error('Global email sending is OFF. Flip the toggle first.');
      return;
    }
    const ok = window.confirm(
      `Send this broadcast to ${eligibleCount} recipient${
        eligibleCount === 1 ? '' : 's'
      }? This is not reversible.`
    );
    if (!ok) return;
    setSendLoading(true);
    try {
      const r = await api.post('/api/admin/email/broadcast', {
        subject,
        html: bodyHtml,
        audience,
        dry_run: false,
        confirm: true,
        attachments: attachments.length
          ? attachments.map(a => ({ filename: a.filename, content: a.content, content_type: a.content_type }))
          : undefined,
      });
      setSendResult(r.data);
      if (r.data?.sending_disabled) {
        toast.error(r.data.message || 'Sending was disabled server-side');
      } else if (r.data?.success) {
        toast.success(
          `Broadcast complete — sent ${r.data.sent ?? 0}, failed ${r.data.failed ?? 0}`
        );
        // Reset the dry-run gate so a second blast requires another check.
        setEligibleCount(null);
      } else {
        toast.error('Broadcast returned an unexpected response');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSendLoading(false);
    }
  };

  // -------- Sandboxed preview (data: URL keeps it origin-less) ------
  const previewSrc = useMemo(() => {
    if (!bodyHtml.trim()) return '';
    try {
      return `data:text/html;charset=utf-8,${encodeURIComponent(bodyHtml)}`;
    } catch {
      return '';
    }
  }, [bodyHtml]);

  // -------- Access guard --------------------------------------------
  if (!isSuperAdmin) {
    return (
      <div
        data-testid="broadcast-access-denied"
        className="max-w-2xl mx-auto mt-16 bg-white border border-gray-200 rounded-2xl p-8 text-center"
      >
        <Lock className="w-12 h-12 mx-auto text-rose-500 mb-3" />
        <h2 className="text-xl font-bold text-gray-900 mb-1">Super-admin only</h2>
        <p className="text-gray-600 text-sm">
          The Email Broadcast console can only be used by super-admins.
        </p>
      </div>
    );
  }

  const canConfirmSend =
    eligibleCount != null && !sendLoading && !!status?.sending_enabled;

  return (
    <div className="space-y-6" data-testid="broadcast-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-emerald-600" />
            Email Broadcast
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Compose, preview, and deliver marketing emails to everyone on the GEMS list — members and waitlist.
          </p>
        </div>
        <button
          onClick={refreshStatus}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
          title="Refresh status"
        >
          <RefreshCw className={`w-4 h-4 text-gray-600 ${statusLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sending status banner */}
      {status && (
        <StatusToggle status={status} onToggle={handleToggle} saving={toggleSaving} />
      )}

      {/* Compose + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Compose</h2>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. GEMS Live is live — come play"
              data-testid="broadcast-subject"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Body (HTML)
            </span>
            <textarea
              ref={bodyTextareaRef}
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder={`<h1>Hey there</h1>\n<p>GEMS Live is live — jump in, join a table, and start playing.</p>`}
              rows={14}
              data-testid="broadcast-body"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <span className="text-xs text-gray-500 mt-1 block">
              The unsubscribe footer + one-click header are appended automatically by the mailer.
            </span>
          </label>

          {/* Feb 2026 — inline image + file attachment toolbar */}
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3" data-testid="broadcast-media-toolbar">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => inlineImgInputRef.current?.click()}
                disabled={uploadingImage}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-60"
                data-testid="broadcast-add-image-btn"
              >
                <ImageIcon className="w-4 h-4" />
                {uploadingImage ? 'Uploading…' : 'Add image / flier (inline)'}
              </button>
              <input
                ref={inlineImgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleAddInlineImage}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => attachInputRef.current?.click()}
                disabled={attachments.length >= 3}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 disabled:opacity-60"
                data-testid="broadcast-add-attachment-btn"
                title={attachments.length >= 3 ? 'Max 3 attachments' : 'Attach a PDF or image'}
              >
                <Paperclip className="w-4 h-4" />
                Attach file ({attachments.length}/3)
              </button>
              <input
                ref={attachInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                multiple
                onChange={handleAddAttachment}
                className="hidden"
              />
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Attachments can lower deliverability — for fliers, prefer an inline image.
            </p>
            {attachments.length > 0 && (
              <ul className="space-y-1.5" data-testid="broadcast-attachment-list">
                {attachments.map((a, idx) => (
                  <li key={`${a.filename}-${idx}`} className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1.5 text-sm" data-testid={`broadcast-attachment-${idx}`}>
                    <Paperclip className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-xs text-gray-500 shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                    <span className="text-xs text-gray-400 shrink-0">{a.content_type.replace('application/','').replace('image/','img/')}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      className="p-0.5 rounded hover:bg-rose-50 text-rose-600 shrink-0"
                      aria-label={`Remove ${a.filename}`}
                      data-testid={`broadcast-attachment-remove-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleTestSend}
              disabled={testSendLoading}
              data-testid="broadcast-test-send-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {testSendLoading ? 'Sending…' : `Send test to me (${admin?.email || 'admin'})`}
            </button>
            <label
              className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none px-2 py-1 rounded border border-gray-200 bg-white"
              title="Off: mock log only (preview default). On: real Resend send to your own inbox (1 per 15 min)."
            >
              <input
                type="checkbox"
                checked={deliverReal}
                onChange={(e) => setDeliverReal(e.target.checked)}
                data-testid="broadcast-test-send-deliver-real"
                className="rounded border-gray-300 text-rose-600 focus:ring-rose-500"
              />
              Deliver a real email (not just a mock log)
            </label>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Preview runs on the <strong>mock</strong> provider — test-sends are logged to <code>db.email_logs</code> and deliver nothing unless you tick the box above.
          </p>
        </div>

        {/* Preview */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-500" />
            Live preview
          </h2>
          {previewSrc ? (
            <iframe
              key={previewSrc}
              title="broadcast-preview"
              data-testid="broadcast-preview"
              sandbox=""
              src={previewSrc}
              className="w-full h-[420px] rounded-lg border border-gray-200 bg-white"
            />
          ) : (
            <div className="w-full h-[420px] rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 text-sm">
              Start typing HTML to see the preview
            </div>
          )}
          <p className="text-xs text-gray-500">
            Preview is sandboxed (no scripts, no origin) so it can&apos;t affect this page.
          </p>
        </div>
      </div>

      {/* Two-step send */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Send</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Step 1 */}
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Step 1</div>
            <div className="font-medium text-gray-900 mb-2">Check recipients (dry-run)</div>
            <p className="text-xs text-gray-500 mb-3">
              Counts every address GEMS holds — all members with an email plus waitlist sign-ups, one email per address.
              Anyone who unsubscribed, bounced or complained is always left out. No email is sent.
            </p>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="broadcast-audience">Audience</label>
            <select
              id="broadcast-audience"
              value={audience}
              onChange={(e) => { setAudience(e.target.value); setEligibleCount(null); setAudienceInfo(null); }}
              data-testid="broadcast-audience-select"
              className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="everyone">Everyone — all members + waitlist</option>
              <option value="members">Members only (accounts)</option>
              <option value="waitlist">Waitlist only (no account yet)</option>
              <option value="opted_in">Ticked the sign-up box + verified (old rule)</option>
            </select>
            <button
              type="button"
              onClick={handleDryRun}
              disabled={dryRunLoading || !subject.trim() || !bodyHtml.trim()}
              data-testid="broadcast-dryrun-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              {dryRunLoading ? 'Checking…' : 'Check recipients'}
            </button>
            {eligibleCount != null && (
              <div
                className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                data-testid="broadcast-eligible-count"
              >
                <strong>{eligibleCount}</strong>{' '}
                eligible recipient{eligibleCount === 1 ? '' : 's'}{' '}
                {audienceInfo?.counts && (
                  <span className="block text-xs text-gray-500 mt-0.5" data-testid="broadcast-audience-breakdown">
                    {audienceInfo.counts.members} member{audienceInfo.counts.members === 1 ? '' : 's'} · {audienceInfo.counts.waitlist_only} waitlist-only
                    {' '}· left out: {audienceInfo.counts.unsubscribed} unsubscribed, {audienceInfo.counts.bounced_or_blocked} bounced/blocked, {audienceInfo.counts.invalid} invalid
                  </span>
                )}
                {audienceInfo && audienceInfo.mailing_address_set === false && (
                  <span className="block text-xs text-red-700 mt-1" data-testid="broadcast-address-missing">
                    Sending is blocked: add your business postal address as the MARKETING_MAILING_ADDRESS environment
                    variable (street address, P.O. box or registered agent), then restart the backend. US law requires
                    it in the footer of every marketing email.
                  </span>
                )}
                {audienceInfo?.capped && (
                  <span className="block text-xs text-amber-700 mt-0.5">
                    Over the {audienceInfo.cap.toLocaleString()} per-send cap — the first {audienceInfo.cap.toLocaleString()} will receive this send.
                  </span>
                )}
                {dryRunAt && (
                  <span className="block text-xs text-gray-400 mt-0.5">
                    checked {new Date(dryRunAt).toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Step 2 */}
          <div
            className={`border rounded-xl p-4 ${
              canConfirmSend ? 'border-rose-300 bg-rose-50' : 'border-gray-200'
            }`}
          >
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Step 2</div>
            <div className="font-medium text-gray-900 mb-2">
              Confirm &amp; send
            </div>
            {!status?.sending_enabled && (
              <div className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1 mb-3 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                <span>Global sending is OFF — flip the toggle above before sending.</span>
              </div>
            )}
            {eligibleCount == null ? (
              <p className="text-xs text-gray-500">
                Run <strong>Step 1</strong> first — the send button appears once we know the count.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleConfirmSend}
                disabled={!canConfirmSend}
                data-testid="broadcast-send-btn"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                  canConfirmSend
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                <Send className="w-4 h-4" />
                {sendLoading
                  ? 'Sending…'
                  : `Send to ${eligibleCount} recipient${eligibleCount === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>

        {sendResult && (
          <div
            data-testid="broadcast-send-result"
            className={`rounded-lg border p-3 text-sm ${
              sendResult.sending_disabled
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {sendResult.sending_disabled ? (
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>{sendResult.message || 'Sending is disabled server-side.'}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                <span>
                  Broadcast complete — <strong>sent {sendResult.sent ?? 0}</strong>,{' '}
                  failed {sendResult.failed ?? 0} · broadcast id{' '}
                  <code className="text-xs bg-white px-1 rounded">
                    {(sendResult.broadcast_id || '').slice(0, 8)}
                  </code>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailBroadcastPage;
